/**
 * Shared PDF View control for invoices.
 *
 * Used in InvoicesPage, BROSRevenuePage PaymentsTab, and BROSRecordsList
 * (Dashboard Invoices tab) so they cannot drift.
 *
 * Fetches PDFs through the authenticated API client (axios with Authorization
 * header, responseType: 'blob') and renders them in a right-side panel.
 *
 * Bros invoices are fetched from BR OS directly (via the backend proxy).
 * Xero is no longer the PDF source for bros invoices.
 *
 * Handles two data shapes:
 *   - Main invoices collection: has id, source, invoice_number
 *   - Raw bros_invoices collection: has bros_id, no source, no id
 *
 * Props:
 *   invoice      - invoice object
 *   pdfMatchInfo - optional { matched, reason } from batch pre-check
 *   variant      - "icon" (compact, for table rows) | "button" (full, for modals)
 *   className    - optional extra classes
 */

import { useState, useCallback } from 'react';
import { Eye, FileX, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { invoicesAPI } from '../lib/api';
import { cn } from '../lib/utils';
import { PdfViewerPanel } from './PdfViewerPanel';
import { detectPdfSource, resolveInvoiceKey, hasPdfArtifact } from '../lib/invoicePdfSource';

const REASON_LABELS = {
  'invoice_not_found': 'Invoice not found',
  'not_found': 'Invoice not found',
  'no_renderable_data': 'Invoice not found',
  'unsupported_invoice_source': 'Unsupported source',
  'bros_pdf_fetch_failed': 'BR OS PDF fetch failed',
  'bros_not_configured': 'BR OS not configured',
  'bros_invoice_missing_id': 'Missing invoice ID',
  'xero_invoice_missing_external_id_or_tenant': 'Xero ref incomplete',
  'xero_not_connected': 'Xero not connected',
  'xero_pdf_fetch_failed': 'Xero PDF fetch failed',
  'xero_token_key_mismatch': 'Xero token expired',
  'unrecognized_file_url': 'PDF link not recognized',
  'no_google_credentials': 'Google account not connected',
  'drive_pdf_fetch_failed': 'Drive PDF fetch failed',
};

function getReasonLabel(reason) {
  if (!reason) return 'No PDF available';
  return REASON_LABELS[reason] || reason;
}

/**
 * Parse error detail from an axios blob-response error.
 * When responseType is 'blob' and the server returns a JSON error (4xx/5xx),
 * axios stores the response body as a Blob in err.response.data. We read it
 * back as text and extract the machine-readable reason.
 */
async function parseErrorDetail(err) {
  const data = err.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      const reason = json.detail?.reason || json.detail;
      if (typeof reason === 'string') return getReasonLabel(reason);
      if (typeof reason === 'object' && reason.reason) return getReasonLabel(reason.reason);
    } catch {
      /* blob was not JSON, fall through */
    }
  }
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return getReasonLabel(detail);
  if (typeof detail === 'object' && detail?.reason) return getReasonLabel(detail.reason);
  return 'Failed to load PDF';
}

export function InvoicePdfButton({ invoice, pdfMatchInfo, variant = 'icon', className }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [renderSource, setRenderSource] = useState(null);
  const [mediaType, setMediaType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const pdfSource = detectPdfSource(invoice);
  const invoiceKey = resolveInvoiceKey(invoice);
  const extension = typeof mediaType === 'string' && mediaType.startsWith('image/')
    ? mediaType.slice('image/'.length).replace('jpeg', 'jpg')
    : 'pdf';
  const filename = `invoice-${invoice?.invoice_number || invoiceKey || 'unknown'}.${extension}`;

  const handleClose = useCallback(() => {
    setPanelOpen(false);
    if (pdfObjectUrl && pdfObjectUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfObjectUrl);
    }
    setPdfObjectUrl(null);
    setRenderSource(null);
    setMediaType(null);
    setError(null);
  }, [pdfObjectUrl]);

  const handleView = useCallback(async (e) => {
    e.stopPropagation();

    setLoading(true);
    setError(null);
    setPanelOpen(true);

    try {
      const response = pdfSource === 'gmail'
        ? await invoicesAPI.fetchAttachment(invoiceKey)
        : await invoicesAPI.fetchPdf(invoiceKey);
      const url = URL.createObjectURL(response.data);
      setPdfObjectUrl(url);
      setRenderSource(response.headers?.['x-invoice-render-source'] || null);
      // Prefer the blob's own type: it is set from the response
      // Content-Type and survives when the custom header is not exposed.
      setMediaType(response.data?.type || response.headers?.['x-invoice-media-type'] || null);
    } catch (err) {
      const msg = await parseErrorDetail(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [pdfSource, invoiceKey, invoice]);

  // Pre-populated match status says unmatched: show disabled control
  if (pdfMatchInfo && pdfMatchInfo.matched === false) {
    return <DisabledControl reason={pdfMatchInfo.reason} variant={variant} className={className} />;
  }

  // Determine if this invoice can have a PDF (shared with
  // reconciliationArtifact.js's chip-vs-detail-view decision, see
  // lib/invoicePdfSource.js). Bros invoices (both source:"bros" and raw
  // bros_invoices) are always viewable: the backend fetches PDFs from
  // BR OS directly, no Xero link required.
  if (!hasPdfArtifact(invoice)) {
    return <DisabledControl reason="No PDF available" variant={variant} className={className} />;
  }

  return (
    <>
      <ViewControl variant={variant} className={className} loading={loading} onClick={handleView} />
      <PdfViewerPanel
        open={panelOpen}
        onClose={handleClose}
        pdfUrl={pdfObjectUrl}
        filename={filename}
        loading={loading}
        error={error}
        renderSource={renderSource}
        mediaType={mediaType}
      />
    </>
  );
}

function ViewControl({ variant, className, loading, onClick }) {
  if (variant === 'icon') {
    return (
      <button
        onClick={onClick}
        className={cn('inline-flex items-center gap-1 text-muted-foreground hover:text-primary', className)}
        title="View PDF"
        data-testid="invoice-pdf-view"
        disabled={loading}
      >
        {loading
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Eye className="h-3.5 w-3.5" />}
      </button>
    );
  }

  // "button" variant for modals/dialogs
  return (
    <Button
      size="sm"
      variant="outline"
      onClick={onClick}
      disabled={loading}
      data-testid="invoice-pdf-view"
      className={className}
    >
      {loading
        ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        : <Eye className="h-4 w-4 mr-2" />}
      View PDF
    </Button>
  );
}

function DisabledControl({ reason, variant, className }) {
  const label = getReasonLabel(reason);

  if (variant === 'icon') {
    return (
      <span
        className={cn('inline-flex items-center gap-1 text-muted-foreground/40', className)}
        title={label}
        data-testid="invoice-pdf-disabled"
      >
        <FileX className="h-3.5 w-3.5" />
        <span className="text-[10px] hidden sm:inline">{label}</span>
      </span>
    );
  }

  // "button" variant for modals/dialogs
  return (
    <Button disabled size="sm" variant="outline" className={className} data-testid="invoice-pdf-disabled">
      <FileX className="h-4 w-4 mr-2" />
      {label}
    </Button>
  );
}
